/**
 * Refund request — the buyer's self-service return, as state. Zero I/O: the
 * decode pipeline is the view's own (device-local, no network), and
 * `refund.sync.ts` (per app package) is the sole answerer, creating the
 * `refundRequests/{id}` document on the first decoded token, rewriting
 * `qrTokens` on every add and every remove, and landing the confirm CAS.
 *
 * FOUR CELLS, one writer each, because they answer four independent
 * questions and a monolith would make the reconciler's own writes race the
 * device's:
 *
 *   refundFiles      what the buyer picked and what the DEVICE made of it —
 *                    written only through the actions below, all of them
 *                    driven by the view's gestures and its decode pipeline
 *   refundRequest    what the RESOLVER answered, projected off the document
 *   refundConfirm    where the one click stands, before the document knows
 *   refundEventId    which event this page is scoped to
 *
 * A recognised token with no matching row in `refundRequest.tickets` is, by
 * definition, still resolving — that derivation IS the per-row spinner, and
 * it needs no field of its own. Past the stall window the same derivation
 * reads `stalled` instead, because a trigger that died must show as an
 * answer the buyer can act on rather than a spinner that never stops.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import type { Locale } from "@momo/i18n";
import { RefundRequest } from "@momo/types";
import { Subject } from "rxjs";
import type { QrToken, StillDecode } from "../../checkin/scan/decode/still.ts";
import { workspaceCard } from "../../workspace/workspace.state.ts";
import { landingState, type PublishedEvent } from "../landing/landing.state.ts";
import { type RefundWindowPhase, refundWindowPhase } from "./window.state.ts";

// ── what the device knows ──────────────────────────────────────────────────

/** One picked file's own state, before the resolver has said anything about
 *  it. Only `recognised` contributes a token to the document. */
export type RefundFileState =
	| "decoding"
	| "recognised"
	| "duplicate"
	| "no-code"
	| "not-ours"
	| "wrong-type"
	| "over-cap"
	| "unfinished"
	| "reader-failed"
	| "removing";

export type RefundFile = {
	readonly id: string;
	/** The buyer's own file name — `ticket-2.png` is what this deployment's
	 *  fulfillment mail attaches, so it is a real-world label, not an id. */
	readonly fileName: string;
	/** 1-based page, for a PDF the buyer printed their tickets into. */
	readonly page: number | null;
	readonly state: RefundFileState;
	/** Present on `recognised` and on `duplicate` — the value submitted, and
	 *  the key the resolver's rows come back on. */
	readonly token: QrToken | null;
	/** A square thumbnail of the very bytes that were decoded, encoded by the
	 *  decoder. It is what carries a row's identity forward onto the verdict,
	 *  which arrives keyed only by an opaque token. */
	readonly preview: string | null;
};

export const refundFiles = state<readonly RefundFile[]>(
	"storefront/refund/files",
).memory([]);

// ── what the resolver answered ─────────────────────────────────────────────

/** One row of the document's own `tickets[]`, taken off the entity so a
 *  reshape there is a compile error here rather than a blank amount. */
export type RefundTicketRow = NonNullable<
	RefundRequest.RefundRequest["tickets"]
>[number];
export type RefundOutcome = NonNullable<RefundRequest.RefundRequest["outcome"]>;
export type RefundVerdict = RefundTicketRow["verdict"];

/** How long the resolver's answer has been outstanding. `slow` is a
 *  reassurance beat, not a second failure: the row keeps its spinner and its
 *  good ground and only says out loud that it is still working, because
 *  `ANSWER_STALL_MS` is two minutes and a row that reads identically at
 *  second five and second ninety reads as a hang. `stalled` is the point the
 *  sweep itself would have acted by, so an answer still missing there is
 *  dead rather than slow, and the row offers its `×`. */
export type RefundAnswerWait = "waiting" | "slow" | "stalled";

export type RefundRequestData = {
	readonly tickets: readonly RefundTicketRow[];
	readonly refundableTotal: number | null;
	readonly confirmed: boolean;
	readonly outcome: RefundOutcome | null;
	/** No entity counterpart: it is a fact about elapsed time on this page,
	 *  which nothing on the document records. */
	readonly answer: RefundAnswerWait;
};

const noRequest: RefundRequestData = {
	tickets: [],
	refundableTotal: null,
	confirmed: false,
	outcome: null,
	answer: "waiting",
};

export const refundRequest = state<RefundRequestData>(
	"storefront/refund/request",
).memory(noRequest);

/** Where the one click stands before the document itself knows. `failed`
 *  rolls the page back to the list with a message rather than stranding the
 *  buyer on a spinner for a write that was refused. */
export type RefundConfirmStatus = "idle" | "confirming" | "failed";

export const refundConfirm = state<RefundConfirmStatus>(
	"storefront/refund/confirm",
).memory("idle");

// ── which event ────────────────────────────────────────────────────────────

export const refundEventId = state<string | null>(
	"storefront/refund/event",
).memory(null);

// ── the channels the reconciler answers ────────────────────────────────────

/** The buyer's own pick on the cold-entry list. The reconciler folds it and
 *  the URL's own `?e=` into `refundEventId`, so that cell has one writer. */
export const refundEventPicked$ = new Subject<string>();
/** The one click. */
export const refundConfirm$ = new Subject<void>();
/** "Return more tickets" — the current document is spent, so the next token
 *  must create a new one. */
export const refundRestart$ = new Subject<void>();

// ── the actions the view drives ────────────────────────────────────────────

/** Every picked file becomes a row at once, before any decoding starts, so
 *  the list appears whole rather than one row at a time. */
export function appendRefundFiles(rows: readonly RefundFile[]): void {
	refundFiles.replace([...refundFiles.value, ...rows]);
}

/**
 * One file's decode outcome, applied against the list AS IT STANDS NOW
 * rather than as it stood when the file was picked — decoding is sequential,
 * so rows have landed in between, and both answers this decides are
 * list-relative:
 *
 *   duplicate — the token is already in the list. A second row for one
 *               ticket would otherwise be priced twice, so it is shown as a
 *               refusal rather than silently dropped.
 *   over-cap  — the token set is already at the resolver's own `in` ceiling.
 */
export function settleRefundFile(id: string, decode: StillDecode): void {
	refundFiles.replace(
		refundFiles.value.map((file) => {
			if (file.id !== id) return file;
			if (decode.kind === "unsupported")
				return { ...file, state: "wrong-type" as const };
			if (decode.kind === "no-code")
				return { ...file, state: "no-code" as const, preview: decode.preview };
			if (decode.kind === "unfinished")
				return {
					...file,
					state: "unfinished" as const,
					preview: decode.preview,
				};
			if (decode.kind === "reader-failed")
				return { ...file, state: "reader-failed" as const };
			if (decode.kind === "not-ours")
				return {
					...file,
					state: "not-ours" as const,
					page: decode.page,
					preview: decode.preview,
				};
			const held = refundFiles.value.filter(
				(other) => other.id !== id && other.state === "recognised",
			);
			return {
				...file,
				state: held.some((other) => other.token === decode.token)
					? ("duplicate" as const)
					: held.length >= RefundRequest.MAX_REFUND_TOKENS
						? ("over-cap" as const)
						: ("recognised" as const),
				token: decode.token,
				page: decode.page,
				preview: decode.preview,
			};
		}),
	);
}

/** First beat of a removal: the row is marked and animates out. Its token
 *  leaves the set immediately, which is what recomputes the total under the
 *  buyer's finger. */
export function markRefundFileLeaving(id: string): void {
	refundFiles.replace(
		refundFiles.value.map((file) =>
			file.id === id ? { ...file, state: "removing" as const } : file,
		),
	);
}

/** Second beat: the row is spliced by the identity re-found now, never by an
 *  index captured before the wait — the array shifts underneath. */
export function dropRefundFile(id: string): void {
	refundFiles.replace(refundFiles.value.filter((file) => file.id !== id));
}

// ── the writes the reconciler drives ───────────────────────────────────────

/** The document as the resolver has written it so far, projected onto the
 *  cell — the reconciler's one write, and `null` is a page with no request
 *  behind it yet. */
export function landRefundRequest(
	request: RefundRequest.RefundRequest | null,
	answer: RefundAnswerWait,
): void {
	refundRequest.replace(
		request === null
			? noRequest
			: {
					tickets: request.tickets ?? [],
					refundableTotal: request.refundableTotal ?? null,
					confirmed: request.confirmedAt != null,
					outcome: request.outcome ?? null,
					answer,
				},
	);
}

export function setRefundConfirm(status: RefundConfirmStatus): void {
	refundConfirm.replace(status);
}

/** The `?e=` the entry link carries and the buyer's own pick on the cold
 *  list both land here, so the cell has one writer. */
export function setRefundEvent(id: string | null): void {
	refundEventId.replace(id);
}

/** One sitting, one surface: a mount starts from nothing rather than
 *  inheriting rows whose document this page is no longer listening to. */
export function resetRefundSurface(): void {
	refundFiles.replace([]);
	landRefundRequest(null, "waiting");
	setRefundConfirm("idle");
}

/** "Return more tickets": the spent document is left behind and the list
 *  starts empty, so the next token creates a fresh request. */
export function restartRefund(): void {
	resetRefundSurface();
	refundRestart$.next();
}

// ── the view-model ─────────────────────────────────────────────────────────

/** What a row reads as, once the device's own state and the resolver's
 *  answer are folded together. Neither `slow` nor `stalled` is a file state
 *  the decoder can produce — both are a recognised token the resolver has not
 *  answered, told apart by how long it has been outstanding. */
export type RefundRowState = RefundFileState | "slow" | "stalled";

export type RefundRow = {
	readonly id: string;
	readonly fileName: string;
	readonly page: number | null;
	readonly preview: string | null;
	readonly state: RefundRowState;
	/** The resolver's answer for this row's token, absent while it resolves. */
	readonly verdict: RefundVerdict | null;
	readonly typeName: string | null;
	readonly amount: number | null;
	readonly waiting: boolean;
};

export const refundRows = computed<readonly RefundRow[]>(() => {
	const request = refundRequest.value;
	return refundFiles.value.map((file) => {
		// A duplicate carries the SAME token as the row already in the list, so
		// looking its verdict up would render one ticket as refundable twice.
		const answer =
			file.state === "recognised" && file.token !== null
				? request.tickets.find((row) => row.qrToken === file.token)
				: undefined;
		const unanswered = file.state === "recognised" && answer === undefined;
		return {
			id: file.id,
			fileName: file.fileName,
			page: file.page,
			preview: file.preview,
			state:
				unanswered && request.answer !== "waiting"
					? request.answer
					: file.state,
			verdict: answer?.verdict ?? null,
			typeName: answer?.typeName ?? null,
			amount: answer?.amount ?? null,
			// A slow answer is still an answer coming, so the row keeps its
			// spinner; only `stalled` gives up and offers the row's own `×`.
			waiting:
				file.state === "decoding" ||
				(unanswered && request.answer !== "stalled"),
		};
	});
});

/** The tokens the document carries — pick order, no duplicates by
 *  construction, and a removed row's token is gone the instant it is marked. */
export const refundTokens = computed<readonly QrToken[]>(() =>
	refundFiles.value.flatMap((file) =>
		file.state === "recognised" && file.token !== null ? [file.token] : [],
	),
);

/** Summed over the rows CURRENTLY in the list, never the document's own
 *  `refundableTotal`: a removal has to move this figure in the same frame the
 *  row starts leaving, and the document does not know about it until the
 *  resolver has answered the rewritten set. */
export const refundableRows = computed<readonly RefundRow[]>(() =>
	refundRows.get().filter((row) => row.verdict === "valid"),
);

export const refundTotal = computed<number>(() =>
	refundableRows.get().reduce((sum, row) => sum + (row.amount ?? 0), 0),
);

/** Which row states are themselves a finished answer. Exhaustive on purpose —
 *  a new `RefundRowState` is a compile error here rather than a silent
 *  "answered" — and every `false` is a ticket whose worth this page does not
 *  know, which "none of this comes back" would be a guess about. */
const STATE_IS_AN_ANSWER = {
	decoding: false,
	recognised: false,
	slow: false,
	stalled: false,
	"over-cap": false,
	// The file may still hold a ticket: neither is an answer about it.
	unfinished: false,
	"reader-failed": false,
	removing: false,
	duplicate: true,
	"no-code": true,
	"not-ours": true,
	"wrong-type": true,
} as const satisfies Record<RefundRowState, boolean>;

/** Every row has reached a terminal answer — the one condition under which
 *  "none of this comes back" is a fact rather than a not-yet. A recognised row
 *  passes on its verdict, which is the only thing that can still arrive for
 *  it; `waiting` alone would not do, because a stalled, over-cap or leaving
 *  row is not waiting for anything and has no answer either. */
export const refundFullyAnswered = computed<boolean>(() =>
	refundRows
		.get()
		.every((row) => row.verdict !== null || STATE_IS_AN_ANSWER[row.state]),
);

export const refundAtCap = computed<boolean>(
	() => refundTokens.get().length >= RefundRequest.MAX_REFUND_TOKENS,
);

// ── the event this page is scoped to ───────────────────────────────────────

/** The event this render is about. A cold entry whose public catalog holds
 *  exactly ONE event with an open window resolves straight to it rather than
 *  asking a question with one answer. */
export const refundTarget = computed<PublishedEvent | null>(() => {
	const catalog = landingState.value.events;
	const picked = refundEventId.value;
	if (picked !== null) return catalog.find((e) => e.id === picked) ?? null;
	const open = catalog.filter((e) => refundWindowPhase(e) === "open");
	return open.length === 1 ? (open[0] ?? null) : null;
});

/** Null while no event is resolved at all — the cold-entry picker's frame. */
export const refundWindow = computed<RefundWindowPhase | null>(() => {
	const target = refundTarget.get();
	return target === null ? null : refundWindowPhase(target);
});

/** Every OTHER event taking returns right now — the cold-entry list, and the
 *  way out of an event whose own window is shut. */
export const refundOpenEvents = computed<readonly PublishedEvent[]>(() => {
	const targetId = refundTarget.get()?.id;
	return landingState.value.events.filter(
		(event) => event.id !== targetId && refundWindowPhase(event) === "open",
	);
});

/** The operator's own note for this event in the buyer's language, absent
 *  when they wrote none — the surface falls back to its own copy. */
export function refundMessage(locale: Locale): string | null {
	return refundTarget.get()?.refund?.message?.[locale] ?? null;
}

/** The statutory contact channel the workspace publishes, or null — a
 *  withheld address beats a fabricated one, so the contact line is absent
 *  rather than pointing nowhere. */
export const refundContactEmail = computed<string | null>(
	() => workspaceCard.value.workspace?.legal?.contactEmail ?? null,
);

// ── where the whole page stands ────────────────────────────────────────────

/** Two faces, not four: the list, and what happens after the one click.
 *  `failed` is both a refused write and a batch that moved no money — the
 *  page must not claim a refund is on its way in either case, and rolling
 *  back to the list is the only move that leaves the buyer something to do. */
export type RefundPhase = "list" | "running" | "settled" | "failed";

export const refundPhase = computed<RefundPhase>(() => {
	const request = refundRequest.value;
	if (refundConfirm.value === "failed" || request.outcome === "failed")
		return "failed";
	if (request.outcome === "done" || request.outcome === "partial")
		return "settled";
	return request.confirmed || refundConfirm.value === "confirming"
		? "running"
		: "list";
});
